import axios from 'axios';
import { getToken } from './auth';
import { compactPurchasePayload } from '../utils/documentItemPayload';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.chamafruits.com';
const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

/**
 * @typedef {Object} PurchaseItem
 * @property {number} varietyId
 * @property {string} varietyName
 * @property {string} [productName]
 * @property {string} metricUnit
 * @property {number} price           - Original unit price.
 * @property {number} [discount]      - Per-unit discount applied at item level.
 * @property {number} quantity        - Quantity purchased for this item.
 * @property {number} [priceIndex]    - Index of the price/variety entry (used by the UI).
 */

/**
 * @typedef {Object} Purchase
 * @property {number} id
 * @property {number|null} clientId
 * @property {number|null} authorId
 * @property {PurchaseItem[]} purchaseItems
 * @property {Object[]} order                - UI ordering metadata for the cart.
 * @property {number} itemsSubtotal          - Subtotal after item-level discounts, before order-wide discount.
 * @property {number} orderDiscountAmount    - Absolute discount applied to the whole order (in pesos).
 * @property {number|null} orderDiscountPercentage - Percentage used to derive the order-wide discount (for display).
 * @property {number} totalAmount            - Final total after all discounts.
 * @property {boolean} isPaid
 * @property {string|null} paymentMethod
 * @property {Object|null} detailForPayment
 * @property {boolean} isDeleted
 * @property {number|null} branchId
 * @property {boolean} madeOnline
 * @property {string|null} documentType
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * Create a new purchase for a branch.
 *
 * The `data` payload should include at least:
 * - `purchaseItems`: `PurchaseItem[]`
 * - Optional discounts: `orderDiscountAmount`, `orderDiscountPercentage`
 * - Client/order metadata: `clientId`, `order`, etc.
 *
 * The backend will compute and persist `itemsSubtotal`, `orderDiscountAmount`,
 * `orderDiscountPercentage`, and `totalAmount` based on the items provided.
 *
 * @param {number|string} branchId
 * @param {Object} data
 * @returns {Promise<{message: string, purchase: Purchase}>}
 */
export const registerPurchase = async (branchId, data) =>
  (await axios.post(
    `${API_URL}/branch/${branchId}/purchases`,
    compactPurchasePayload(data),
    withAuth()
  )).data;

// 📆 Purchases by Date
export const getPaidPurchasesByDate = async (branchId, startDate, endDate, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases`, {
    ...withAuth(),
    params: { startDate, endDate, ...params },
  })).data;

export const getPurchasesByDate = async (branchId, startDate, endDate, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/by-date`, {
    ...withAuth(),
    params: { startDate, endDate, ...params },
  })).data;

export const getPurchasePaymentsByDate = async (branchId, startDate, endDate, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/payments`, {
    ...withAuth(),
    params: { startDate, endDate, ...params },
  })).data;

// 🕗 Pending
export const getPendingPurchases = async (
  branchId,
  startDate,
  endDate,
  hasClientId,
  includeSuspended,
  params = {}
) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/pending`, {
    ...withAuth(),
    params: { startDate, endDate, hasClientId, includeSuspended, ...params },
  })).data;

// 💳 Paid
export const getPaidPurchases = async (branchId, startDate, endDate, hasClientId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/paid`, {
    ...withAuth(),
    params: { startDate, endDate, hasClientId, ...params },
  })).data;

// 👤 By Client
export const getPurchasesByClientId = async (branchId, clientId, params = {}) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/client/${clientId}`, { ...withAuth(), params })).data;

// 🔍 By ID
/**
 * Fetch a single purchase (including discounts and payment info).
 *
 * @param {number|string} branchId
 * @param {number|string} id
 * @returns {Promise<Purchase>}
 */
export const getPurchaseById = async (branchId, id) =>
  (await axios.get(`${API_URL}/branch/${branchId}/purchases/${id}`, withAuth())).data;

// ❌ Delete (Pending only)
export const deletePendingPurchase = async (branchId, id, payload = {}) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/purchases/${id}`, {
    ...withAuth(),
    data: payload,
  })).data;

// ✏️ Update
/**
 * Update an existing purchase.
 *
 * When `data.purchaseItems` is provided, the backend recomputes `itemsSubtotal`,
 * `orderDiscountAmount`, `orderDiscountPercentage`, and `totalAmount`.
 *
 * @param {number|string} branchId
 * @param {number|string} id
 * @param {Partial<Purchase> & { purchaseItems?: PurchaseItem[] }} data
 * @returns {Promise<{message?: string, purchase: Purchase}>}
 */
export const updatePurchase = async (branchId, id, data) =>
  (await axios.patch(
    `${API_URL}/branch/${branchId}/purchases/${id}`,
    compactPurchasePayload(data),
    withAuth()
  )).data;

// 💰 Add purchase payment
export const addPurchasePayment = async (branchId, id, data) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/${id}/payments`, data, withAuth())).data;

// ✏️ Update purchase payment (admin)
export const updatePurchasePayment = async (branchId, paymentId, data) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/purchases/payments/${paymentId}`, data, withAuth())).data;

// ❌ Delete purchase payment (admin)
export const deletePurchasePayment = async (branchId, paymentId) =>
  (await axios.delete(`${API_URL}/branch/${branchId}/purchases/payments/${paymentId}`, withAuth())).data;

// ✅ Mark as Paid
export const markAsPaid = async (branchId, id) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/purchases/${id}/paid`, {}, withAuth())).data;

// ❌ Mark as Unpaid
export const markAsUnpaid = async (branchId, id) =>
  (await axios.patch(`${API_URL}/branch/${branchId}/purchases/${id}/unpaid`, {}, withAuth())).data;

// 🔁 Convert sale to quotation
export const convertPurchaseToQuotation = async (branchId, id, data = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/${id}/convert-to-quotation`, data, withAuth())).data;

// ⏸️ Suspend / ▶️ Resume sales
export const suspendPurchase = async (branchId, id, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/${id}/suspend`, payload, withAuth())).data;

export const resumePurchase = async (branchId, id, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/${id}/resume`, payload, withAuth())).data;

export const reopenPurchase = async (branchId, id) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/${id}/reopen`, {}, withAuth())).data;

export const resumePurchaseByCode = async (branchId, code, payload = {}) =>
  (await axios.post(`${API_URL}/branch/${branchId}/purchases/resume-by-code`, { code, ...payload }, withAuth())).data;
